import { Link } from 'react-router-dom'
import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  LegalLayout,
  Prose,
  Reveal,
} from './kit.jsx'

const TOC = [
  { id: 'privacy', label: 'Privacy Notice' },
  { id: 'terms', label: 'Terms of Service' },
  { id: 'cookies', label: 'Cookie Notice' },
]

export default function PrivacyTermsPage() {
  return (
    <MarketingShell active="">
      <Seo
        title="Privacy, terms & cookies"
        description="How Revailo Sales collects, uses, and protects business contact data — our Privacy Notice, Terms of Service, and Cookie Notice in plain English."
        path="/privacy"
        keywords="Revailo Sales privacy, terms of service, cookie notice, b2b contact data, GDPR, CCPA"
      />

      <PageHero
        eyebrow="Legal"
        title="Privacy, terms & cookies"
        lead="The rules that govern how Revailo Sales handles your account, your outreach, and the business contact data in your workspace. Written to be read, not just filed."
        align="left"
      />

      <Section>
        <Container>
          <LegalLayout toc={TOC}>
            <Reveal>
              <p className="text-[15px] text-dim" style={{ marginBottom: 8 }}>
                Last updated: July 2026
              </p>
              <p className="text-[15px] text-dim">
                This page covers three documents: our Privacy Notice, our Terms of Service, and
                our Cookie Notice. It applies to revailo-sales.com and the Revailo Sales
                application. It is served at both <Link to="/privacy">/privacy</Link> and{' '}
                <Link to="/terms">/terms</Link>. In this page, &ldquo;Revailo Sales,&rdquo;
                &ldquo;we,&rdquo; and &ldquo;us&rdquo; mean the company operating the service, and
                &ldquo;you&rdquo; means the account holder and their team.
              </p>
            </Reveal>

            <Reveal>
              <Section id="privacy" sm style={{ padding: '24px 0 0' }}>
                <Prose>
                  <h2>Privacy Notice</h2>
                  <p>
                    Revailo Sales is a sales workspace for B2B sales
                    teams. We hold a directory of 10,942 verified company
                    companies (10,942 companies across the verticals you sell into) sourced from The Blue
                    Book, and we help you run email outreach and a deal pipeline against it. This
                    notice explains what data we collect, why, and the choices you have.
                  </p>

                  <h3>Information we collect</h3>
                  <p>We collect the following categories of data:</p>
                  <ul>
                    <li>
                      <strong>Account data.</strong> Your name, work email, company name, role, and
                      password credentials, which are managed through Amazon Cognito. We never store
                      your password in plain text.
                    </li>
                    <li>
                      <strong>Team and workspace data.</strong> The regions you assign, the reps you
                      invite, notes you write, deal stages you set, and the campaigns you build.
                    </li>
                    <li>
                      <strong>Usage and device data.</strong> Log data such as pages viewed,
                      features used, timestamps, IP address, and browser type — used to keep the
                      service secure and to improve it.
                    </li>
                    <li>
                      <strong>Gmail authorization.</strong> If you connect Gmail to send outreach,
                      Google issues us an OAuth access token and refresh token with your explicit
                      consent. We use these only to send the sequences you build and to log the
                      replies that come back. We do not read, index, or mine your broader mailbox.
                    </li>
                    <li>
                      <strong>Licensed business directory.</strong> Company-level business
                      contact records — company name, industry, service area, established year, size,
                      annual volume, and named business contacts. This is business information about
                      companies, not consumer data about private individuals.
                    </li>
                  </ul>

                  <h3>How we use your information</h3>
                  <ul>
                    <li>To provide the workspace — the directory, sequences, pipeline, activity, and analytics.</li>
                    <li>To send email outreach from your connected Gmail account, at your direction.</li>
                    <li>To power the in-app assistant, which answers questions from data already in your workspace.</li>
                    <li>To authenticate users, secure accounts, prevent abuse, and troubleshoot issues.</li>
                    <li>To communicate with you about service updates, billing, and support.</li>
                  </ul>
                  <p>
                    We do not sell your account data, and we do not use the contents of your
                    connected Gmail account for advertising or for training generalized models.
                  </p>

                  <h3>Sharing and subprocessors</h3>
                  <p>
                    We share data only with vendors that help us run the service, under contract and
                    only as needed:
                  </p>
                  <ul>
                    <li>
                      <strong>Amazon Web Services (AWS)</strong> — hosting, databases (DynamoDB),
                      serverless compute (Lambda), and authentication (Cognito). Your workspace data
                      lives in AWS data centers in the United States.
                    </li>
                    <li>
                      <strong>Google</strong> — the Gmail API, used solely to send and receive the
                      outreach you configure, under the scopes you approve.
                    </li>
                  </ul>
                  <p>
                    A current list of subprocessors and the data each one handles is available in
                    our <Link to="/dpa">Data Processing Addendum</Link>. We may also disclose data if
                    required by law, or in connection with a merger or acquisition, in which case
                    this notice will continue to govern the data transferred.
                  </p>

                  <h3>Security</h3>
                  <p>
                    Data is encrypted in transit with TLS and at rest in AWS. Access to production
                    systems is restricted to authorized personnel, gated behind Cognito-backed
                    authentication and least-privilege roles. Gmail tokens are stored encrypted and
                    scoped to sending and reply-logging only. No system is perfectly secure, but we
                    work to protect your data and will notify you of a breach affecting your account
                    as required by law.
                  </p>

                  <h3>Retention and deletion</h3>
                  <p>
                    We keep account and workspace data for as long as your account is active. When
                    you disconnect Gmail, the associated tokens are revoked and deleted. When you
                    close your account, we delete or de-identify your workspace data within 30 days,
                    except where we must retain limited records to meet legal, tax, or security
                    obligations. You can request deletion at any time by contacting us.
                  </p>

                  <h3>Your rights and choices</h3>
                  <p>
                    Depending on where you are located, you may have the right to access, correct,
                    export, or delete your personal data, and to object to or restrict certain
                    processing. You can disconnect Gmail at any time from your settings, which stops
                    all sending immediately. To exercise any right, reach us through our{' '}
                    <Link to="/contact">contact page</Link>; we respond within the timeframes
                    required by applicable law, including the GDPR and CCPA where they apply.
                  </p>

                  <h3>Business contact data</h3>
                  <p>
                    The business directory describes companies and their business roles. If you are
                    a contact listed in that directory and want your record reviewed or removed,
                    contact us and we will handle the request under applicable law.
                  </p>
                </Prose>
              </Section>
            </Reveal>

            <Reveal>
              <Section id="terms" sm style={{ padding: '24px 0 0' }}>
                <Prose>
                  <h2>Terms of Service</h2>
                  <p>
                    These terms are a contract between you and Revailo Sales. By creating an account
                    or using the service, you agree to them. If you are agreeing on behalf of a
                    company, you represent that you have authority to bind that company.
                  </p>

                  <h3>Your account</h3>
                  <p>
                    You are responsible for the activity under your account and for keeping your
                    credentials secure. You must provide accurate information, be at least 18, and
                    use the service for legitimate business purposes only. You are responsible for
                    the reps you invite and the outreach they send from your workspace.
                  </p>

                  <h3>Acceptable use</h3>
                  <p>You agree not to:</p>
                  <ul>
                    <li>Send unlawful, deceptive, harassing, or unsolicited bulk email that violates anti-spam laws.</li>
                    <li>Resell, redistribute, or scrape the directory data outside your own contracted use of the service.</li>
                    <li>Share your login, exceed the seats on your plan, or circumvent usage limits.</li>
                    <li>Reverse engineer, disrupt, or attempt to gain unauthorized access to the service or its infrastructure.</li>
                    <li>Upload malware, or use the service to violate the rights of any person or company.</li>
                  </ul>

                  <h3>Email outreach and compliance</h3>
                  <p>
                    You send outreach from your own Gmail account, as yourself, and you are the
                    sender of record. You are responsible for complying with the laws that apply to
                    your messages — including the CAN-SPAM Act and similar rules — such as honoring
                    opt-outs, identifying yourself accurately, and including a valid physical
                    address. You must also comply with Google&rsquo;s Gmail and API terms. We may
                    suspend sending that puts your account or our infrastructure at risk.
                  </p>

                  <h3>The directory license</h3>
                  <p>
                    We grant you a limited, non-exclusive, non-transferable right to use the Blue
                    Book directory data inside Revailo Sales to run your own sales outreach and
                    pipeline. The data is licensed, not sold, and remains ours or our licensors&rsquo;.
                    This license ends when your subscription ends.
                  </p>

                  <h3>Fees and subscription</h3>
                  <p>
                    Paid plans are billed in advance on the cycle shown at checkout and renew
                    automatically until canceled. Fees are non-refundable except where required by
                    law. We may change pricing with notice that takes effect on your next renewal.
                    Failure to pay may result in suspension of your account.
                  </p>

                  <h3>Disclaimers</h3>
                  <p>
                    The service is provided &ldquo;as is&rdquo; and &ldquo;as available.&rdquo; We do
                    not guarantee that directory records are complete or error-free, that every
                    contact is current, or that outreach will produce any particular result. To the
                    fullest extent permitted by law, we disclaim all warranties, express or implied,
                    including merchantability, fitness for a particular purpose, and
                    non-infringement.
                  </p>

                  <h3>Limitation of liability</h3>
                  <p>
                    To the fullest extent permitted by law, Revailo Sales will not be liable for any
                    indirect, incidental, special, consequential, or punitive damages, or for lost
                    profits, revenue, or data. Our total liability for any claim arising out of the
                    service is limited to the amount you paid us in the 12 months before the event
                    giving rise to the claim.
                  </p>

                  <h3>Termination</h3>
                  <p>
                    You may cancel at any time from your account settings. We may suspend or
                    terminate access for violation of these terms, non-payment, or risk to the
                    service. On termination, your right to use the service and the directory data
                    ends, and we handle your data as described in the Privacy Notice.
                  </p>

                  <h3>Governing law</h3>
                  <p>
                    These terms are governed by the laws of the State of Delaware, United States,
                    without regard to conflict-of-law rules. Disputes will be resolved in the state
                    or federal courts located in Delaware, and you consent to their jurisdiction. If
                    any provision is found unenforceable, the rest remains in effect.
                  </p>

                  <h3>Changes to these terms</h3>
                  <p>
                    We may update these terms from time to time. Material changes will be posted here
                    with a new &ldquo;Last updated&rdquo; date, and where required we will notify you.
                    Continuing to use the service after changes take effect means you accept the
                    revised terms.
                  </p>
                </Prose>
              </Section>
            </Reveal>

            <Reveal>
              <Section id="cookies" sm style={{ padding: '24px 0 0' }}>
                <Prose>
                  <h2>Cookie Notice</h2>
                  <p>
                    We use a small number of cookies and similar technologies to keep the service
                    working and to understand how it is used. We keep them minimal and do not use
                    cookies to sell your data.
                  </p>

                  <h3>Cookies we use</h3>
                  <ul>
                    <li>
                      <strong>Essential.</strong> Session and authentication cookies set by Amazon
                      Cognito to keep you signed in and to protect against cross-site request
                      forgery. The service does not work without these.
                    </li>
                    <li>
                      <strong>Preferences.</strong> A cookie that remembers your choices, such as
                      whether you use the dark or light theme, so the workspace looks the same on
                      your next visit.
                    </li>
                    <li>
                      <strong>Analytics.</strong> First-party, aggregated measurement of feature
                      usage and performance, used to improve the product. These do not build
                      cross-site advertising profiles.
                    </li>
                  </ul>

                  <h3>Managing cookies</h3>
                  <p>
                    You can clear or block cookies in your browser settings. Blocking essential
                    cookies will sign you out and prevent the app from functioning. We honor
                    &ldquo;Do Not Track&rdquo; signals for non-essential analytics where your browser
                    sends them.
                  </p>
                </Prose>
              </Section>
            </Reveal>
          </LegalLayout>
        </Container>
      </Section>

      <Section sm>
        <Container>
          <Reveal>
            <p className="text-[15px] text-dim" style={{ textAlign: 'center' }}>
              Processing data on behalf of your business? See our{' '}
              <Link to="/dpa" style={{ color: 'var(--mk-accent-dark)', fontWeight: 500 }}>
                Data Processing Addendum
              </Link>
              . Questions about anything here?{' '}
              <Link to="/contact" style={{ color: 'var(--mk-accent-dark)', fontWeight: 500 }}>
                Contact us
              </Link>
              .
            </p>
          </Reveal>
        </Container>
      </Section>
    </MarketingShell>
  )
}
